import dayjs from 'dayjs';
import _ from 'lodash';
import ApiConstants from 'src/common/helpers/ApiConstants';
import { isInAbnormalRange } from 'src/common/helpers/Utils';

export const RAW_MAX_RANGE_HOURS = 6;
export const HOURLY_BUCKET_SECONDS = 3600;
// Hourly rows for day D are written by the nightly aggregation job shortly
// after D ends (cron 00:30 JST). Until this local hour we must assume
// yesterday's aggregates don't exist yet and serve yesterday from raw data,
// otherwise the graph renders a silent 24h hole between midnight and the
// end of the aggregation run.
export const AGGREGATES_READY_HOUR = 1;
// #5845: reverted to the pre-2.9.1 behaviour at the client's request — every
// range is served from raw data, as PROD (FE 2.9.0) does. The hourly-aggregate
// selection below is kept, not deleted: CLS C-003-001 「グラフ描画/CSV出力の
// 速度向上」 is still a live requirement, and the USDM leaves its 対象条件 as
// 「別途検討」. Re-enable only once those conditions are agreed with the client.
export const HOURLY_AGGREGATE_ENABLED = false;

/**
 * Decide whether the requested range is served from raw data or from
 * hourly aggregates (plus a raw tail for the not-yet-aggregated days).
 * - duration <= 6h -> raw (existing path, unchanged)
 * - duration > 6h but no day is aggregated yet -> raw
 * - otherwise -> hourly aggregates for
 *   [date(start) .. min(date(end), lastAggregatedDay)] plus a raw tail
 *   covering [rawStart = aggEnd + 1 day .. end] when the range extends
 *   beyond the aggregated days.
 */
export const selectTrendDataSource = (
  startTime,
  endTime,
  now = new Date(),
  enabled = HOURLY_AGGREGATE_ENABLED
) => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  const todayStart = dayjs(now).startOf('day');

  // Before AGGREGATES_READY_HOUR the nightly job may not have written
  // yesterday's rows yet -> the newest reliably aggregated day is the day
  // before yesterday.
  const lastAggregatedDay = todayStart.subtract(
    dayjs(now).hour() < AGGREGATES_READY_HOUR ? 2 : 1,
    'day'
  );

  const durationHours = end.diff(start, 'hour', true);
  const aggStart = start.startOf('day');
  const endDay = end.startOf('day');
  const aggEnd = endDay.isBefore(lastAggregatedDay)
    ? endDay
    : lastAggregatedDay;

  if (!enabled || durationHours <= RAW_MAX_RANGE_HOURS
      || aggStart.isAfter(aggEnd)) {
    return { mode: 'raw' };
  }

  // First moment not covered by aggregates; the raw tail spans [rawStart, end].
  const rawStart = aggEnd.add(1, 'day');
  return {
    mode: 'hourly',
    aggStart: aggStart.format('YYYY-MM-DD'),
    aggEnd: aggEnd.format('YYYY-MM-DD'),
    includesRawTail: !end.isBefore(rawStart),
    rawStart: rawStart.format('YYYY-MM-DD HH:mm'),
  };
};

/**
 * Channels eligible for the hourly-aggregate endpoint. The backend rejects
 * the WHOLE request (400) if any requested channelId has dataType !== 0, so
 * this must stay stricter than the [0, 1, 2] display filter used when
 * assembling the graph datasets.
 */
export const selectAggregateChannels = (channels) =>
  (channels || []).filter((c) => c.dataType === 0 && !!c.dataUnit);

/**
 * Build the GET data/aggregated URL for one device, or null when the device
 * has no channel the aggregate endpoint accepts (-> skip the request).
 */
export const buildHourlyAggregateUrl = (deviceId, channels, source) => {
  const aggregateChannels = selectAggregateChannels(channels);
  if (!aggregateChannels.length) {
    return null;
  }
  return `${ApiConstants.BASE_URL}${
    ApiConstants.DATA_VIEW
  }?type=hourly&start=${source.aggStart}&end=${
    source.aggEnd
  }&deviceId=${deviceId}&channelIds=${aggregateChannels
    .map((c) => c.channelId)
    .join('&channelIds=')}`;
};

/**
 * Map hourly aggregate rows ({date, min, avg, max, sum, count}) to Dygraphs
 * customBars points [Date, [min, avg, max]] plus running stats.
 * Buckets that don't overlap [startMs, endMs) are dropped.
 */
export const mapAggregatedRowsToPoints = (rows, startMs, endMs) => {
  const stats = { min: null, max: null, sum: 0, count: 0 };
  const points = [];
  (rows || []).forEach((row) => {
    const bucketStart = dayjs(row.date).toDate().getTime();
    if (
      bucketStart >= endMs ||
      bucketStart + HOURLY_BUCKET_SECONDS * 1000 <= startMs
    ) {
      return;
    }
    const lo = Number(row.min);
    const mid = Number(row.avg);
    const hi = Number(row.max);
    points.push([new Date(bucketStart), [lo, mid, hi]]);
    if (stats.min === null || lo < stats.min) stats.min = lo;
    if (stats.max === null || hi > stats.max) stats.max = hi;
    stats.sum += Number(row.sum);
    stats.count += Number(row.count);
  });
  points.sort((a, b) => a[0] - b[0]);
  return { points, stats };
};

/**
 * Raw-tail records (the not-yet-aggregated days: today, plus yesterday
 * before the nightly job has run) -> zero-height triplets per channel.
 * Deliberately NO sno-gap reconstruction and NO alarm counting (metaData
 * shows '---' in aggregate mode); abnormal values still break the line via
 * NaN.
 */
export const buildRawTailChannelData = (
  rawRecords,
  validChannels,
  startTime,
  endTime
) => {
  let records = (rawRecords || []).filter(
    (x) =>
      x.sno !== 0 &&
      dayjs(x.lts) >= dayjs(startTime) &&
      dayjs(x.lts) <= dayjs(endTime)
  );
  records = _.orderBy(
    _.uniqBy(
      records.map((r) => ({
        ...r,
        lts: dayjs(r.lts).format('YYYY/MM/DD HH:mm:ss'),
      })),
      'lts'
    ),
    ['lts', 'sno'],
    ['asc', 'asc']
  );
  const result = {};
  validChannels.forEach(({ channelId }) => {
    const field = channelId.toLowerCase(); // 'CH1' -> 'ch1'
    const stats = { min: null, max: null, sum: 0, count: 0 };
    const points = [];
    records.forEach((rec) => {
      const raw = rec[field];
      if (raw === undefined) return;
      const v = isInAbnormalRange(raw) ? NaN : raw;
      points.push([new Date(rec.lts), [v, v, v]]);
      if (typeof v === 'number' && !Number.isNaN(v)) {
        if (stats.min === null || v < stats.min) stats.min = v;
        if (stats.max === null || v > stats.max) stats.max = v;
        stats.sum += v;
        stats.count += 1;
      }
    });
    result[channelId] = { points, stats };
  });
  return result;
};

/**
 * Combine aggregate-part stats and today-part stats into the trend-graph
 * metaData shape. missing/numOfAlarms are not derivable from aggregates ->
 * null (rendered as '---').
 */
export const combineAggregateMeta = (a, t) => {
  const count = (a.count || 0) + (t.count || 0);
  const mins = [a.min, t.min].filter((v) => v !== null);
  const maxs = [a.max, t.max].filter((v) => v !== null);
  return {
    missing: null, // not derivable from aggregates -> '---'
    numOfAlarms: null, // ditto
    minValue: mins.length ? Math.min(...mins) : null,
    maxValue: maxs.length ? Math.max(...maxs) : null,
    avgValue: count ? (a.sum + t.sum) / count : null, // exact weighted avg (rows carry sum & count)
    numOfRecords: count,
  };
};
